import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import { useCardStore } from "../store/cardStore";
import { useThemeStore, type ThemeMode } from "../store/themeStore";
import GridCanvas from "../components/GridCanvas";
import AddCardModal from "../components/AddCardModal";
import CardEditModal from "../components/CardEditModal";
import TutorialModal from "../components/TutorialModal";
import SettingsModal from "../components/SettingsModal";
import { Eye, EyeOff, Plus, Settings, ArrowUp } from "lucide-react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import GlobalAudioPlayer from "../components/GlobalAudioPlayer";
import { useAudioPlayerStore } from "../store/audioPlayerStore";
import { GRID_COLS } from "../types";
import { calculateCellSize } from "../utils/gridLayout";

declare global {
  interface Window {
    openEditorTutorial?: () => void;
  }
}

const LOAD_STEPS = [
  "Authenticating",
  "Loading cards",
  "Preparing grid",
  "Applying theme",
  "Ready",
];

function EditorLoader() {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (step >= LOAD_STEPS.length - 1) return;
    const delay = step === 0 ? 400 : 300 + Math.random() * 400;
    const t = setTimeout(
      () => setStep((s) => Math.min(s + 1, LOAD_STEPS.length - 1)),
      delay,
    );
    return () => clearTimeout(t);
  }, [step]);

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center gap-6">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        className="w-6 h-6 border border-white/20 border-t-white/80"
      />
      <div className="flex flex-col items-center gap-1">
        {LOAD_STEPS.slice(0, step + 1).map((label, i) => (
          <motion.p
            key={label}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: i === step ? 1 : 0.3, y: 0 }}
            transition={{ duration: 0.3 }}
            className="font-serif text-xs tracking-wider uppercase"
          >
            {label}
          </motion.p>
        ))}
      </div>
    </div>
  );
}

export default function EditorPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuthStore();
  const {
    fetchCards,
    fetchSubpages,
    editMode,
    setEditMode,
    selectedCardId,
    setSelectedCard,
    cards,
    subpages,
    currentPageId,
    setCurrentPageId,
  } = useCardStore();
  const { colorPalette } = useThemeStore();

  const [showAddModal, setShowAddModal] = useState(false);
  const [showTutorialModal, setShowTutorialModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [themeProfileCard, setThemeProfileCard] = useState<any>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [playerMaxWidth, setPlayerMaxWidth] = useState(0);
  const { setQueueFromCards } = useAudioPlayerStore();

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/");
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCards();
      fetchSubpages();
    }
  }, [isAuthenticated, fetchCards, fetchSubpages]);

  const switchPage = async (pageId: number | null) => {
    if (pageId === currentPageId) return;
    setCurrentPageId(pageId);
    await fetchCards(pageId);
  };

  const syncThemeFromSource = (sourceCard: any | null) => {
    if (!sourceCard) return;

    const nextTheme = (sourceCard.content as any)?.theme as ThemeMode | undefined;
    const nextPaletteSeedRaw = (sourceCard.content as any)?.paletteSeed;
    const nextPaletteSeed =
      typeof nextPaletteSeedRaw === "number" && Number.isFinite(nextPaletteSeedRaw)
        ? nextPaletteSeedRaw
        : 0;
    const nextColorPaletteRaw = (sourceCard.content as any)?.colorPalette;
    const nextColorPalette =
      typeof nextColorPaletteRaw === "number" && nextColorPaletteRaw >= 0 && nextColorPaletteRaw <= 3
        ? nextColorPaletteRaw
        : 0;
    const nextCardRadiusRaw = (sourceCard.content as any)?.cardRadius;
    const nextCardRadius = nextCardRadiusRaw === "sharp" ? "sharp" : "rounded";

    if (nextTheme) {
      document.documentElement.setAttribute("data-theme", nextTheme);
      document.documentElement.setAttribute("data-palette", String(nextColorPalette));
      useThemeStore.setState({
        theme: nextTheme,
        paletteSeed: nextPaletteSeed,
        colorPalette: nextColorPalette,
        cardRadius: nextCardRadius,
      });
    }
  };

  useEffect(() => {
    setThemeProfileCard(null);
    if (!isAuthenticated) return;
    if (currentPageId === null) return;

    let cancelled = false;
    const fetchThemeSource = async () => {
      try {
        const res = await axios.get("/api/cards?page_id=null");
        const list = (res.data?.cards || []) as any[];
        const pc = list.find((c) => c.card_type === "profile") || null;
        if (!cancelled) setThemeProfileCard(pc);
      } catch {
        if (!cancelled) setThemeProfileCard(null);
      }
    };

    fetchThemeSource();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, currentPageId]);

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const key = `nok.editorTutorialSeen.user.${user.id}`;
    const seen = localStorage.getItem(key) === "1";
    if (!seen) {
      setShowTutorialModal(true);
    }

    window.openEditorTutorial = () => {
      setShowTutorialModal(true);
    };

    return () => {
      if (window.openEditorTutorial) {
        delete window.openEditorTutorial;
      }
    };
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (currentPageId !== null) return;
    const mainProfileCard = cards.find((c) => c.card_type === "profile") || null;
    syncThemeFromSource(mainProfileCard);
  }, [cards, currentPageId]);

  useEffect(() => {
    if (currentPageId === null) return;
    // On subpages, only sync from main-page profile source once it changes.
    // Do not tie this to subpage cards updates.
    syncThemeFromSource(themeProfileCard || null);
  }, [currentPageId, themeProfileCard]);

  useEffect(() => {
    setQueueFromCards(cards);
  }, [cards, setQueueFromCards]);

  useEffect(() => {
    const syncPlayerWidth = () => {
      const reserved = editMode ? 160 : 40;
      const cellSize = calculateCellSize(window.innerWidth, window.innerHeight, reserved);
      setPlayerMaxWidth(cellSize * GRID_COLS);
    };

    syncPlayerWidth();
    window.addEventListener("resize", syncPlayerWidth);
    return () => window.removeEventListener("resize", syncPlayerWidth);
  }, [editMode]);

  useEffect(() => {
    if (isAuthenticated && user && !user.subdomain) {
      setShowSettingsModal(true);
    }
  }, [isAuthenticated, user]);

  // Apply data-palette attribute
  useEffect(() => {
    document.documentElement.setAttribute("data-palette", String(colorPalette));
  }, [colorPalette]);

  // Track window scroll as well since the body might be scrolling
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      const mainEl = document.getElementById("main-scroll-container");
      const mainScroll = mainEl ? mainEl.scrollTop : 0;
      setShowScrollTop(scrollY > 100 || mainScroll > 100);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const cardsLoading = useCardStore((s) => s.isLoading);
  const showAppLoader =
    authLoading || (isAuthenticated && cardsLoading && cards.length === 0);

  if (showAppLoader) {
    return <EditorLoader />;
  }
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[var(--app-bg)] text-[var(--app-fg)] flex items-center justify-center px-6 text-center">
        <div className="space-y-4">
          <p className="text-nok-gray-300 font-serif">
            Bu sayfayı görmek için giriş yapmalısın.
          </p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 border border-nok-gray-700 text-nok-gray-300 font-serif hover:border-nok-gray-500 transition-colors"
          >
            Ana Sayfaya Dön
          </button>
        </div>
      </div>
    );
  }

  const selectedCard = cards.find((c) => c.id === selectedCardId);

  return (
    <div className="min-h-screen bg-[var(--app-bg)] text-[var(--app-fg)] flex flex-col">
      <div className="fixed top-[72px] left-4 z-[45] pointer-events-none select-none text-[10px] uppercase tracking-[0.18em] font-serif text-[var(--app-fg)]/24">
        {editMode ? "Edit Mode" : "Preview Mode"}
      </div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[var(--app-bg)] border-b border-[var(--surface-border)] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-1 min-w-0">
          {user?.subdomain ? (
            <a
              href={`https://${user.subdomain}.oda.bio`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-serif text-xl font-bold text-[var(--app-fg)] shrink-0 hover:opacity-70 transition-opacity"
              title="View your profile"
            >
              {user.subdomain}.oda.bio
            </a>
          ) : (
            <h1 className="font-serif text-xl font-bold text-[var(--app-fg)] shrink-0">
              oda.bio
            </h1>
          )}
          {subpages.length > 0 && (
            <div className="flex items-center gap-0.5 ml-2 overflow-x-auto">
              <button
                onClick={() => switchPage(null)}
                className={`px-2 py-1 font-serif text-[11px] whitespace-nowrap rounded transition-colors ${currentPageId === null
                    ? "bg-[var(--app-fg)]/10 text-[var(--app-fg)]"
                    : "text-[var(--app-fg)]/40 hover:text-[var(--app-fg)]/70"
                  }`}
              >
                Main
              </button>
              {subpages.map((sp) => (
                <button
                  key={sp.id}
                  onClick={() => switchPage(sp.id)}
                  className={`px-2 py-1 font-serif text-[11px] whitespace-nowrap rounded transition-colors ${currentPageId === sp.id
                      ? "bg-[var(--app-fg)]/10 text-[var(--app-fg)]"
                      : "text-[var(--app-fg)]/40 hover:text-[var(--app-fg)]/70"
                    }`}
                >
                  /{sp.slug}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setEditMode(!editMode)}
            className="p-2 text-[var(--app-fg)]/70 hover:text-[var(--app-fg)] transition-colors"
            title={editMode ? "Preview Mode" : "Edit Mode"}
          >
            {editMode ? <Eye size={20} /> : <EyeOff size={20} />}
          </button>
          <button
            onClick={() => setShowSettingsModal(true)}
            className="p-2 text-[var(--app-fg)]/70 hover:text-[var(--app-fg)] transition-colors"
          >
            <Settings size={20} />
          </button>
        </div>
      </header>

      {/* Canvas */}
      <main
        id="main-scroll-container"
        className="flex-1 pt-14 pb-20 overflow-auto"
        style={{ overscrollBehavior: editMode ? "none" : "auto" }}
        onScroll={(e) => setShowScrollTop(e.currentTarget.scrollTop > 100)}
      >
        <GridCanvas />
      </main>

      {/* Add Button & Scroll to Top */}
      <AnimatePresence>
        {editMode && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-0 left-0 right-0 p-4 bg-[var(--app-bg)] border-t border-[var(--surface-border)] flex gap-3"
          >
            <button
              onClick={() => setShowAddModal(true)}
              className="flex-1 py-4 bg-[var(--surface-1)] border-2 border-[var(--surface-border)] opacity-80 font-serif flex items-center justify-center gap-2 hover:opacity-100 transition-colors"
            >
              <Plus size={24} />
            </button>
            {showScrollTop && (
              <button
                onClick={() => {
                  const main = document.getElementById("main-scroll-container");
                  if (main) main.scrollTo({ top: 0, behavior: "smooth" });
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="md:hidden w-[56px] flex-shrink-0 bg-[var(--surface-1)] border-2 border-[var(--surface-border)] opacity-80 flex items-center justify-center hover:opacity-100 transition-colors"
                title="Scroll to top"
              >
                <ArrowUp size={24} />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modals */}
      <AddCardModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
      />

      <TutorialModal
        isOpen={showTutorialModal}
        onClose={() => {
          if (user) {
            localStorage.setItem(`nok.editorTutorialSeen.user.${user.id}`, "1");
          }
          setShowTutorialModal(false);
        }}
      />

      {selectedCard && (
        <CardEditModal
          card={selectedCard}
          isOpen={!!selectedCardId}
          onClose={() => setSelectedCard(null)}
        />
      )}

      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />

      <GlobalAudioPlayer
        bottomOffset={editMode ? 124 : 38}
        forceHidden={showSettingsModal}
        maxWidthPx={playerMaxWidth}
      />
    </div>
  );
}
